import React from 'react';
import { FlatList, Linking, Platform, ScrollView } from 'react-native';
import { useTheme } from 'react-native-paper';
import { t, Trans } from '@lingui/macro';
import { GoftinoSnippet } from '@mohsen007/react-goftino';
import styled from 'styled-components';

import Container from '@src/components/Container';
import Divider from '@src/components/Divider';
import TourHeaderRight from '@src/components/iconComponets/TourHeaderRight';
import ReadMoreContent from '@src/components/ReadMoreContent';
import Separator from '@src/components/Separator';
import { useChatWithSupport } from '@src/components/Support';
import { Body1, H2 } from '@src/components/Typography';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { indexKeyExtractor } from '@src/configuration/constants';
import { durationTour, formatPrice } from '@src/helpers';
import { CheckAccess } from '@src/helpers/hasAccessMember';
import useAllStatus from '@src/hooks/useAllStatus';
import useAnimatedHeader from '@src/hooks/useAnimatedHeader';
import useHasPrime from '@src/hooks/useHasPrime';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n } from '@src/i18n';
import Share from '@src/modules/Share';
import useTour from '@src/screens/Tour/useTour';
import { Business } from '@src/types';

import BusinessCard from './BusinessCard';
import DiscountCodeButton from './DiscountCodeButton';
import Exclude from './Exclude';
import Include from './Include';
// import Map from './Map';
import Programs from './Programs';
import StartBusinessCard from './StartBusinessCard';
import {
  BoldText,
  Buy,
  BuyView,
  Column,
  Content,
  ContentImage,
  CoverTour,
  Icon,
  LargeText,
  LeaderButton,
  NormalText,
  Row,
  RowPrice,
  SpacedRow,
  SupportButton,
  TinyText,
  Title,
} from './styled';
import styles from './styles';
import { Props } from './types';

const Screen: React.FC<Props> = ({ route, navigation }) => {
  const handleScroll = useAnimatedHeader();
  const id = route.params.id;
  const tour = useTour(id);
  const snackBar = useSnackBar();
  // const hasPrime = useHasPrime();
  const allStatus = useAllStatus();

  const isWeb = React.useMemo(() => Platform.OS === 'web', []);
  const [hideButton, setHideButton] = React.useState(false);
  // const tt = useChatWithSupport(`${tour.data?.id},${tour.data?.title}`);

  const handleBuyPress = React.useCallback(async () => {
    if (!tour.data) {
      return;
    }
    try {
      await Linking.openURL(tour.data.orderURL);
    } catch (error) {
      snackBar.show({ content: i18n._(t`Can't open link`) });
    }

    // FIXME: `youtopin.com` domain returns `false` in `Linking.canOpenURL`
    // but opens without any error
    // const supported = await Linking.canOpenURL(tour.data.orderURL);
    // if (supported) {
    //   await Linking.openURL(tour.data.orderURL);
    // } else {
    //   snackBar.show({ content: i18n._(t`Can't open link`) });
    // }
  }, [snackBar, tour.data]);

  const handleSharePress = React.useCallback(() => {
    try {
      if (!tour) {
        return;
      }
      Share.open({
        title: i18n._(t`Share tour`),
        url: tour.data?.shareURL,
      }).then(() => {
        if (Platform.OS === 'web') {
          snackBar.show({
            content: i18n._(t`Tour link copied to your clipboard`),
          });
        }
      });
    } catch (e) {
      console.log(e);
    }
  }, [snackBar, tour]);

  const handleChatWithSupportPress = React.useCallback(() => {
    navigation.navigate('Support', {});
  }, [navigation]);

  const handleGetDiscountPress = React.useCallback(() => {
    navigation.navigate('PrimePlans', {});
  }, [navigation]);

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => <TourHeaderRight OnSharePress={handleSharePress} />,
    });
  }, [handleSharePress, navigation]);

  if (tour.loading || !tour.data) {
    return null;
  }

  const source = tour?.data?.cover?.uri
    ? tour.data.cover
    : require('@src/assets/images/tourPlaceholder.png');
  const price = formatPrice(tour.data.price);
  const regex = /[۰-۹]/g;

  const en2faDigits = (s: string) =>
    s.replace(/[0-9]/g, (w) => String.fromCharCode(w.charCodeAt(0) + 1728));

  return (
    <>
      <VisibleStatusBar />
      <Container>
        <ScrollView
          onScroll={handleScroll}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
        >
          <ContentImage>
            <CoverTour source={source} />
          </ContentImage>
          <Column>
            <Content>
              <Title>{en2faDigits(tour.data.title)}</Title>
              <SpacedRow>
                <NormalText>{en2faDigits(tour.data.duration)}</NormalText>
                <LeaderButton>
                  <TinyText textColor={'#403F3F'}>
                    <Trans>Leader Code</Trans>
                  </TinyText>
                  <NormalText marginSize={4}>
                    {en2faDigits(tour.data.leaderId)}
                  </NormalText>
                </LeaderButton>
              </SpacedRow>
            </Content>
            <Divider horizontalPadding={32} />
            <Content>
              <Row>
                <Icon source={require('@src/assets/images/calendar.png')} />
                <NormalText textColor="#757575" marginSize={4}>
                  <Trans>Time of Tour </Trans>
                </NormalText>
                <LargeText>
                  {durationTour(tour.data?.beginDate, tour.data?.endDate)}
                </LargeText>
              </Row>
              <Row>
                <Icon source={require('@src/assets/images/group.png')} />
                <NormalText textColor="#757575" marginSize={4}>
                  <Trans>group Capacity</Trans>
                </NormalText>
                <LargeText>
                  {en2faDigits(tour.data.capacity.toString())}
                </LargeText>
              </Row>

              <Row>
                <Icon source={require('@src/assets/images/vehicle.png')} />
                <NormalText textColor="#757575" marginSize={4}>
                  <Trans>Carry and transfer</Trans>
                </NormalText>
                <LargeText>{tour.data.vehicle}</LargeText>
              </Row>
              <Row>
                <Icon source={require('@src/assets/images/hiking.png')} />
                <NormalText textColor="#757575" marginSize={4}>
                  <Trans>difficulty</Trans>
                </NormalText>
                <LargeText>{en2faDigits(tour.data.difficulty)}</LargeText>
              </Row>
            </Content>
            <Divider horizontalPadding={32} />
            <Content>
              <BoldText>
                <Trans>About this tour</Trans>
              </BoldText>
              <ReadMoreContent>
                <Body1>{tour.data.description}</Body1>
              </ReadMoreContent>
            </Content>

            {/* {tour.data?.beginDate && tour.data?.endDate && (
              <DatesContainer>
                <Column>
                  <Column>
                    <Subtitle1>
                      <Trans>Start Date </Trans>
                    </Subtitle1>
                    <Body2>{tour.data.beginDate.format('dddd jD jMMMM')}</Body2>
                  </Column>
                  <Subtitle1>
                    <Trans>Tour duration</Trans>:{' '}
                    <Body2>{tour.data.duration}</Body2>
                  </Subtitle1>
                </Column>
                <Column>
                  <Column>
                    <Subtitle1>
                      <Trans>End Date</Trans>
                    </Subtitle1>
                    <Body2>{tour.data.endDate.format('dddd jD jMMMM')}</Body2>
                  </Column>
                  <Subtitle1>
                    <Trans>Capacity</Trans>:
                    <Body2>
                      {tour.data.capacity}&nbsp;
                      <Trans>people</Trans>
                    </Body2>
                  </Subtitle1>
                </Column>
              </DatesContainer>
            )} */}

            <Programs items={tour.data.programs} />
            <Divider />
            {(tour.data.includes.length > 0 ||
              tour.data.excludes.length > 0) && (
              <Content>
                {tour.data.includes?.map((include, index) => (
                  <Include key={index}>{include}</Include>
                ))}
                {tour.data.excludes?.map((include, index) => (
                  <Exclude key={index}>{include}</Exclude>
                ))}
              </Content>
            )}

            {tour.data.businesses.length !== 0 && (
              <>
                <Content>
                  <H2>
                    <Trans>Activity locations</Trans>
                  </H2>
                </Content>
                <FlatList<Business>
                  data={tour.data.businesses}
                  initialNumToRender={3}
                  ListHeaderComponent={
                    tour.data.startBusiness ? (
                      <StartBusinessCard item={tour.data.startBusiness} />
                    ) : null
                  }
                  renderItem={({ item }) => <BusinessCard item={item} />}
                  horizontal
                  contentContainerStyle={styles.contentContainer}
                  showsHorizontalScrollIndicator={false}
                  removeClippedSubviews
                  ItemSeparatorComponent={Separator}
                  keyExtractor={indexKeyExtractor}
                />
              </>
            )}
            <Content>
              {allStatus && tour.data.booking > 0 && (
                <DiscountCodeButton
                  hasPrime={allStatus && CheckAccess(allStatus) > 0}
                  onConfirm={handleGetDiscountPress}
                  onGetCodeSuccessful={handleBuyPress}
                />
              )}
              {/* <Buy onPress={handleBuyPress}>
                <Trans>discount Tour</Trans>
              </Buy> */}
            </Content>
          </Column>
          {/*<Map geopoint={tour.data.geopoint} />*/}
        </ScrollView>
      </Container>
      <Divider horizontalPadding={32} />
      {tour.data.booking > 0 ? (
        <RowPrice>
          <Row>
            <Title>{price} </Title>
            <TinyText>
              <Trans>Toman </Trans>
            </TinyText>
          </Row>
          <BuyView>
            <Buy onPress={handleBuyPress}>
              <LargeText textColor={'#ffffff'}>
                <Trans>Buy Tour</Trans>
              </LargeText>
            </Buy>
          </BuyView>
        </RowPrice>
      ) : (
        <RowPrice>
          <Title>
            {' '}
            <Trans>No Capacity</Trans>{' '}
          </Title>
        </RowPrice>
      )}
      {!isWeb ? (
        <SupportButton icon="headset" onPress={handleChatWithSupportPress} />
      ) : (
        <>
          <GoftinoSnippet
            goftinoKey={'20S8eL'}
            onReady={() => {
              window.Goftino?.setWidget({
                hasIcon: false,
              });
            }}
            onClose={() => {
              setHideButton(false);
            }}
          />
          <button
            onClick={() => {
              window.Goftino?.open();
              setHideButton(true);
            }}
            style={{
              opacity: 1,
              overflow: 'hidden',
              marginRight: 16,
              outline: 'none',
              visibility: 'visible',
              transition: 'all 0.15s linear',
              color: 'white',
              backgroundColor: 'transparent',
              position: 'absolute',
              bottom: 68,
              width: 40,
              height: 40,
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <img
              style={{ width: 40, height: 40 }}
              src={require('@src/assets/images/Goftino.png')}
            />
          </button>
        </>
      )}
    </>
  );
};

Screen.displayName = 'Tour';

export default Screen;
